import { IFieldEditor } from '@/shared/types/fieldEditor';
import { ApolloQueryResult, OperationVariables } from '@apollo/client';
import { AIFieldType } from '@trace-8/trace8-schema';
import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext } from 'react';

export interface FieldEditorContextProps {
  allFields: IFieldEditor[] | null;
  currentField: IFieldEditor | null;
  ids?: {
    funnelId?: string | null;
    funnelStaticId?: string | null;
  };
  investmentId: string | null;
  editFieldPageType: keyof typeof AIFieldType;
  shortTextAdditionalProperties?: {
    maxLength: number;
  };
  setIsAiPaneOpen: Dispatch<SetStateAction<boolean>>;
  setCurrentField: (field: IFieldEditor | null) => void;
  setEditFieldPageType: (type: string | null) => void;
  mainQueryRefetch: (variables?: Partial<OperationVariables>) => Promise<ApolloQueryResult<any>>;
  handleUpdateFieldAnswer: ({
    id,
    recordId,
    value,
    commentary
  }: {
    id: string;
    recordId: string;
    value: string;
    commentary?: string;
  }) => Promise<void>;
  handleUploadAttachment: ({
    file,
    recordId,
    fieldId,
    fieldAnswerId
  }: {
    file: File;
    recordId: string;
    fieldId: string;
    fieldAnswerId: string;
  }) => Promise<{
    id: string;
    url: string;
    assetKey: string;
  }>;
  handleDeleteAttachment: ({ id }: { id: string }) => Promise<void>;
  handleClose: () => void;
}

const FieldEditorContext = createContext<FieldEditorContextProps | undefined>(undefined);

export const FieldEditorProvider: React.FC<{ children: ReactNode } & FieldEditorContextProps> = ({ children, ...props }) => (
  <FieldEditorContext.Provider value={props}>{children}</FieldEditorContext.Provider>
);

export const useFieldEditorContext = () => {
  const context = useContext(FieldEditorContext);
  if (!context) throw new Error('useFieldEditorContext must be used within FieldEditorProvider');
  return context;
};
